import {
    Attribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    Host,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    SkipSelf
} from '@angular/core';
import {ControlContainer, FormGroup, NgForm, ValidationErrors} from '@angular/forms';
import {BehaviorSubject, combineLatest, distinctUntilChanged, Observable, of, pairwise, Subject, switchMap} from 'rxjs';
import {filter, map, startWith, takeUntil} from 'rxjs/operators';
import {fastObjectEquals} from '~shared/utils/utils';
import {KeyValuesPipe} from '~shared/ng/pipes/KeysPipe';
import {AsyncPipe, NgFor} from '@angular/common';

@Component({
    standalone: true,
    selector: 'validation-error',
    templateUrl: './validation-error.component.html',
    host: {
        'aria-atomic': 'true',
    },
    styleUrls: [
        'validation-error.component.scss',
    ],
    imports: [KeyValuesPipe, AsyncPipe, NgFor],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationErrorComponent implements DoCheck, OnInit, OnDestroy {
    public static CLASS_VISIBLE = 'visible';

    public readonly errors$: Observable<ValidationErrors | null>;

    protected readonly for$ = new BehaviorSubject<string | null>(null);
    protected readonly doCheck$ = new Subject<void>();

    // Form control state
    protected readonly submitted$: Observable<boolean>;
    protected readonly invalid$: Observable<boolean>;
    protected readonly dirty$: Observable<boolean>;

    // Computed hidden state!
    protected readonly visible$: Observable<boolean>;

    protected readonly us$ = new Subject<void>();

    public constructor(
        @Attribute('aria-live')
        ariaLive: string,
        protected readonly changeDetectorRef: ChangeDetectorRef,
        protected readonly elementRef: ElementRef,
        @Host() @Optional()
        protected readonly ngForm: NgForm | null,
        @Host() @SkipSelf()
        protected readonly parentControlContainer: ControlContainer,
        protected readonly renderer: Renderer2,
    ) {

        if (!ariaLive) {
            this.elementRef.nativeElement.setAttribute('aria-live', 'polite');
        }

        // since form does not emit anything when is reset, this is the only way how to implement submitted state.
        this.submitted$ = this.ngForm
            ? this.doCheck$.pipe(
                startWith(undefined),
                map(() => this.ngForm.submitted),
            )
            : of (false);

        const control$ = this.for$.pipe(
            distinctUntilChanged(),
            switchMap(($: string) => {
                if (typeof $ === 'string') {
                    return this.doCheck$.pipe(
                        startWith(void 0),
                        map(() => {
                            return (this.parentControlContainer.control as FormGroup)?.controls[$];
                        }),
                    );
                }
                return of(null);
            }),
            distinctUntilChanged()
        );

        this.invalid$ = control$.pipe(
            switchMap($control => {
                if ($control) {
                    return $control.statusChanges.pipe(
                        map($ => $ === 'INVALID'),
                        startWith($control.status === 'INVALID'),
                    );
                }
                return of(false);
            }),
            distinctUntilChanged(),
        );

        this.dirty$ = control$.pipe(
            switchMap($control => {
                if ($control) {
                    return this.doCheck$.pipe(
                        startWith(void 0),
                        map(() => $control.dirty),
                    );
                }
                return of(false);
            }),
            distinctUntilChanged(),
        );

        this.errors$ = control$.pipe(
            switchMap($control => {
                if ($control) {
                    return this.doCheck$.pipe(
                        startWith(void 0),
                        map(() => $control.errors),
                    );
                }
                return of(null);
            }),
            distinctUntilChanged(),
        );

        this.visible$ = combineLatest([
            this.invalid$,
            this.dirty$,
            this.submitted$,
        ]).pipe(
            map(([$invalid, $dirty, $submitted]: [boolean, boolean, boolean]): boolean => {
                return $invalid && ($dirty || $submitted);
            }),
            distinctUntilChanged(),
        );
    }

    public ngDoCheck(): void {
        this.doCheck$.next();
    }

    public ngOnInit(): void {
        // Render hidden state.
        this.visible$.pipe(
            takeUntil(this.us$)
        ).subscribe($ => {
            if ($) {
                this.renderer.addClass(this.elementRef.nativeElement, ValidationErrorComponent.CLASS_VISIBLE);
            } else {
                this.renderer.removeClass(this.elementRef.nativeElement, ValidationErrorComponent.CLASS_VISIBLE);
            }
        });

        // Mark for check
        this.errors$
            .pipe(
                pairwise(),
                filter(([$a, $b]) => {
                    return fastObjectEquals($a, $b);
                }),
                map($ => $[1]),
                distinctUntilChanged(),
                takeUntil(this.us$)
            )
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        this.doCheck$.complete();
        this.for$.complete();
    }

    @Input()
    public set for($value: string) {
        if (this.for$.value !== $value) {
            this.for$.next($value);
        }
    }
    public get for(): string { return this.for$.value; }
}
